// render-adventures.ts — the "catalog-adventures" renderer: the adventure marketplace pages
// (adventures.php hub + free_adventures.php / paid_adventures.php).
//
// PHP selects raw rows; the browser builds and filters cards in memory. The card is the
// bespoke adventure catalog card: 2/3 cover image (or hover-play header video), title, level-begin →
// level-end badges, an optional credit line, and a price + release-date footer. Cloned element-for-
// element from the legacy options/table_lists/table_list_adventures_catalog_inc.php so the card is
// visually identical; that PHP partial was retired once the catalog pages moved to this renderer.
//
// One generic endpoint serves every page via ?tier= (free|paid|all); tier only flips the price
// predicate. The toolbar search box (data-ft-search-input-id) filters cards by name, instantly and
// client-side. Hub mounts omit the search box and just render their tier.

import type { CatalogAdventuresResponse, CatalogListConfig } from './types';
import { resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';
import { buildEmptyState, emptyStateFromAttributes } from '../shared/empty-state';
import { wireFilterClearButton } from './filter-clear';
import { renderAdventureCardElement } from '../shared/adventure-card';
import { adventureBrowseCard, adventureViewer, validateAdventureList } from './adventure-list-data';

interface BuiltCard { el: HTMLElement; text: string; begin: number; end: number; isFree: boolean; price: number; }

// Read a numeric filter control by element id; '' / missing / NaN → null (no bound). Mirrors render-games.ts.
function readNumField(elementId: string | undefined): number | null {
    if (!elementId) return null;
    const el = document.getElementById(elementId) as HTMLInputElement | null;
    const v = (el?.value ?? '').trim();
    if (v === '') return null;
    const n = parseFloat(v);
    return Number.isNaN(n) ? null : n;
}

// Read a string filter control (e.g. the All/Free/Paid tier <select>) by element id; missing → ''.
function readStrField(elementId: string | undefined): string {
    if (!elementId) return '';
    const el = document.getElementById(elementId) as HTMLInputElement | HTMLSelectElement | null;
    return (el?.value ?? '').trim();
}

// Close the filter panel (slideToggleEl if available, else hide) — same contract as the games/cards/table.
function closePanel(panelId: string | null): void {
    if (!panelId) return;
    if (typeof window.slideToggleEl === 'function') {
        window.slideToggleEl(panelId);
        return;
    }
    const panel = document.getElementById(panelId);
    if (panel) panel.style.display = 'none';
}

export function mountCatalogAdventures(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container) {
        console.error('[catalog-adventures] mount missing [data-ft-catalog-results]', root);
        return;
    }

    const searchInput = config.searchInputId
        ? (document.getElementById(config.searchInputId) as HTMLInputElement | null)
        : null;
    // Optional per-mount empty message (hub pages keep their "No free adventures yet…" flavour).
    const emptyText = root.getAttribute('data-ft-empty-text') || 'No adventures found.';

    const inlineData = readInlinePayload(root);
    if (inlineData) {
        validateAdventureList(inlineData, 'browse');
        hideLoader(root);
        setStatus(root, '');
        build(root, container, config, inlineData, searchInput, emptyText);
        return;
    }

    void (async () => {
        const loadingTimer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
            });
            // Read as text first so a stray PHP warning / HTML 500 surfaces a useful message rather
            // than an opaque JSON.parse error (same guard as render-games.ts / render-table.ts).
            const raw = await res.text();
            let data: CatalogAdventuresResponse;
            try {
                data = JSON.parse(raw) as CatalogAdventuresResponse;
            } catch {
                throw new Error(`Non-JSON response (HTTP ${res.status}): ${raw.slice(0, 300)}`);
            }
            const errData = data as unknown as { error?: string; detail?: string };
            if (!res.ok || errData.error) {
                throw new Error(errData.detail || errData.error || `HTTP ${res.status}`);
            }
            clearTimeout(loadingTimer);
            hideLoader(root);
            setStatus(root, '');
            build(root, container, config, data, searchInput, emptyText);
        } catch (e) {
            clearTimeout(loadingTimer);
            hideLoader(root);
            console.error('[catalog-adventures] fetch/render failed', e);
            setStatus(root, '');
            container.innerHTML = '';
            const msg = document.createElement('div');
            msg.className = 'col-12 text-center text-muted py-4';
            msg.textContent = 'Could not load adventures. Please try again.';
            container.appendChild(msg);
        }
    })();
}

function readInlinePayload(root: HTMLElement): CatalogAdventuresResponse | null {
    const payloadId = root.getAttribute('data-ft-payload-id');
    const el = payloadId
        ? document.getElementById(payloadId)
        : root.querySelector<HTMLScriptElement>('script[type="application/json"][data-ft-catalog-payload]');
    if (!el || !el.textContent) return null;
    try {
        const parsed = JSON.parse(el.textContent);
        return parsed && typeof parsed === 'object' ? parsed as CatalogAdventuresResponse : null;
    } catch (e) {
        console.error('[catalog-adventures] inline payload parse failed', e);
        return null;
    }
}

function build(
    root: HTMLElement,
    container: HTMLElement,
    config: CatalogListConfig,
    data: CatalogAdventuresResponse,
    searchInput: HTMLInputElement | null,
    emptyText: string,
): void {
    container.innerHTML = '';

    validateAdventureList(data, 'browse');
    const levelIconHtml = typeof iconMap === 'function' ? iconMap('level', 25, 25, true) : '';

    // Each card is the `col-*` wrapper around the link card (explicit Bootstrap column classes, NOT a
    // bare `.col`) — so unlike the games/rules renderers it brings its own grid column and sits inside
    // a plain `.row` container, matching the legacy adventures partial exactly.
    const built: BuiltCard[] = data.rows.map((row) => {
        const card = adventureBrowseCard(row, adventureViewer());
        const el = renderAdventureCardElement(card, levelIconHtml);
        container.appendChild(el);
        return {
            el,
            text: card.text || (card.name || '').toLowerCase(),
            begin: parseInt(card.level_begin, 10) || 0,
            end: parseInt(card.level_end, 10) || 0,
            isFree: !!card.is_free,
            price: typeof card.price === 'number' ? card.price : (parseFloat(String(card.price)) || 0),
        };
    });

    // Empty-state at the top of the reserved space (shown only when a keyword/filter hides every card,
    // or the tier genuinely has no adventures).
    const emptyEl = document.createElement('div');
    emptyEl.className = 'col-12 text-center text-muted py-5';
    emptyEl.textContent = emptyText;
    emptyEl.style.display = built.length ? 'none' : '';
    container.appendChild(emptyEl);
    const emptySpec = built.length === 0 ? emptyStateFromAttributes(root, emptyText) : null;
    if (emptySpec) {
        emptyEl.className = 'col-12';
        emptyEl.replaceChildren(buildEmptyState(emptySpec));
    }

    // Hydrate <i class="ft-icon"> placeholders (icon_ph) — e.g. the level icon. This renderer doesn't
    // call initTooltips(), so mount icons explicitly (idempotent; footer/template delegation wires hover).
    if (typeof window.ftMountIcons === 'function') {
        try { window.ftMountIcons(container); } catch { /* footer pass covers init */ }
    }

    // Optional filter panel. The field ids come from the mount's data-ft-filter-fields; every branch is
    // guarded by whether its control exists, so a page that omits a control simply skips that filter:
    //   level_start — keep adventures whose STARTING level (level_begin) is >= this value
    //   level_end   — keep adventures whose ENDING level (level_end) is <= this value
    //   price_start — keep adventures whose price is >= this value (paid_adventures.php)
    //   price_end   — keep adventures whose price is <= this value (paid_adventures.php)
    //   tier        — 'free' | 'paid' | '' / 'all' (the adventures.php hub's Free/Paid/All <select>)
    const ff = config.filterFields || {};

    const applyFilter = (): void => {
        const keyword = (searchInput?.value || '').trim().toLowerCase();
        const levelStart = readNumField(ff.level_start);
        const levelEnd = readNumField(ff.level_end);
        const priceStart = readNumField(ff.price_start);
        const priceEnd = readNumField(ff.price_end);
        const tier = readStrField(ff.tier).toLowerCase();
        const filtersActive = !!keyword || levelStart !== null || levelEnd !== null
            || priceStart !== null || priceEnd !== null
            || (tier === 'free' || tier === 'paid');
        let visible = 0;
        for (const c of built) {
            const show = (!keyword || c.text.includes(keyword))
                && (levelStart === null || c.begin >= levelStart)
                && (levelEnd === null || c.end <= levelEnd)
                && (priceStart === null || c.price >= priceStart)
                && (priceEnd === null || c.price <= priceEnd)
                && (tier !== 'free' || c.isFree)
                && (tier !== 'paid' || !c.isFree);
            c.el.style.display = show ? '' : 'none';
            if (show) visible++;
        }
        if (!emptySpec) emptyEl.textContent = filtersActive ? 'No adventures match your filters.' : emptyText;
        emptyEl.style.display = visible === 0 ? '' : 'none';
    };

    // Filter panel: the Apply button (data-ft-filter-button-id) closes the panel + re-filters; the
    // controls also filter live (number inputs on 'input', the tier select on 'change'). Guarded so
    // search-only pages are unaffected.
    if (config.filterButtonId) {
        const btn = document.getElementById(config.filterButtonId);
        btn?.addEventListener('click', () => { closePanel(config.filterPanelId); applyFilter(); });
    }
    wireFilterClearButton(config, applyFilter); // footer "Clear all" (panel stays open)
    for (const id of Object.values(ff)) {
        if (!id) continue;
        const el = document.getElementById(id);
        if (!el) continue;
        el.addEventListener('input', applyFilter);
        el.addEventListener('change', applyFilter);
    }

    // Live keyword search + the house spyglass/clear trailing icons (same wiring as games/cards/table/rules).
    if (searchInput) {
        const box = searchInput.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
        const syncTrailing = (): void => {
            if (!glyph || !clear) return;
            const hasText = (searchInput.value || '').trim().length > 0;
            glyph.classList.toggle('d-none', hasText);
            clear.classList.toggle('d-none', !hasText);
        };
        searchInput.addEventListener('input', () => { syncTrailing(); applyFilter(); });
        if (clear) {
            clear.addEventListener('click', (e) => {
                e.preventDefault();
                searchInput.value = '';
                searchInput.focus();
                syncTrailing();
                applyFilter();
            });
        }
        syncTrailing();
    }

    applyFilter();
}
